export const cmsOrigin = (import.meta.env.VITE_CMS_URL || 'https://cms.apeirochi.com').replace(/\/$/, '');

export interface CmsTag { id: number; name: string }
export interface CmsPost {
  id: number;
  title: string;
  slug: string;
  shortDescription?: string | null;
  publishedAt?: string | null;
  createdAt?: string;
  image?: { url?: string; alt?: string } | number;
  tags?: (CmsTag | number)[];
  contentMarkdown?: string;
  content?: { root?: { children?: LexicalNode[] } };
}
interface LexicalNode { type?: string; text?: string; children?: LexicalNode[] }
export interface IndustryLink {
  id: string;
  url: string;
  title: string;
  description: string;
  date: string;
  tag?: CmsTag | number;
}
export interface IndustryCollection { name: string; slogan: string; links: IndustryLink[] }

export function safeWebUrl(value?: string, base = cmsOrigin): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value, base);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined;
  } catch { return undefined; }
}

export function postImage(post: CmsPost) {
  return typeof post.image === 'object' ? safeWebUrl(post.image?.url) : undefined;
}

export function postTags(post: CmsPost): string[] {
  return (post.tags ?? []).flatMap(tag => typeof tag === 'object' && tag.name ? [tag.name] : []);
}

function nodeText(node: LexicalNode): string {
  return node.text ?? (node.children ?? []).map(nodeText).join('');
}

export function postParagraphs(post: CmsPost): string[] {
  return (post.content?.root?.children ?? []).map(nodeText).filter(Boolean);
}

export function postSummary(post: CmsPost): string {
  return post.shortDescription ?? '';
}

async function readCms<T>(path: string, signal: AbortSignal): Promise<T> {
  // Vite provides this proxy locally; production uses the public backend proxy.
  // Static Web Apps redirects cannot substitute the suffix of an /api/* match.
  const apiBase = import.meta.env.PROD
    ? (import.meta.env.VITE_API_URL || 'https://quickwin-api.apeirochi.com/api').replace(/\/+$/, '').replace(/\/api$/, '')
    : '';
  const url = `${apiBase}/api/bakery-hub/${path}`;
  const cacheKey = `bakery-hub:${url}`;
  // Public editorial content only; refresh after one minute, including across navigation.
  try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
    if (cached && cached.expires > Date.now()) return cached.data as T;
  } catch { /* Storage may be disabled. */ }
  const response = await fetch(url, { signal, credentials: 'omit' });
  if (!response.ok) throw new Error(`CMS request failed (${response.status})`);
  const data = await response.json() as T;
  if (!signal.aborted) {
    try { sessionStorage.setItem(cacheKey, JSON.stringify({ expires: Date.now() + 60_000, data })); }
    catch { /* Storage may be disabled or full. */ }
  }
  return data;
}

export async function fetchLatestPosts(signal: AbortSignal): Promise<CmsPost[]> {
  const params = new URLSearchParams({
    depth: '1', limit: '3', pagination: 'false', sort: '-publishedAt,-createdAt,-id', draft: 'false',
    'where[_status][equals]': 'published',
    'select[title]': 'true', 'select[slug]': 'true', 'select[shortDescription]': 'true',
    'select[publishedAt]': 'true', 'select[createdAt]': 'true', 'select[tags]': 'true', 'select[image]': 'true',
    'populate[tags][name]': 'true', 'populate[media][url]': 'true', 'populate[media][alt]': 'true',
    'populate[media][filename]': 'true',
  });
  return (await readCms<{ docs: CmsPost[] }>(`posts?${params}`, signal)).docs;
}

export function fetchPost(id: number, signal: AbortSignal): Promise<CmsPost> {
  const params = new URLSearchParams({
    depth: '0', draft: 'false', 'select[title]': 'true', 'select[slug]': 'true',
    'select[content]': 'true', 'select[contentMarkdown]': 'true',
  });
  return readCms<CmsPost>(`posts/${id}?${params}`, signal);
}

export function fetchIndustryCollection(signal: AbortSignal) {
  return readCms<IndustryCollection>('link-collections/1?depth=1', signal);
}
